import React, { useEffect } from "react";
import wizardStyle from "../wizardStyle";
import Paper from "@mui/material/Paper";
import FormFader from "../Common/Fader/FormFader";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import Group from "../../Wizard/Group";
import PersonIcon from "../../../assets/icons/provision/toggleable/IconPerson";
import LegalEntityIcon from "../../../assets/icons/contract/toggleable/IconCompany";
import Address from "../Common/Address/Address";
import Person from "../Common/Person/Person";
import { emailDefault, isPhoneNumberDefault, requiredValidator } from "../../../services/validationRules";
import { useForm } from "../../../provider/Form/FormProvider";
import ValidatorTextField from "../../FormFields/ValidatorTextField";
import Typography from "@mui/material/Typography";
import FormLegend from "../Common/FormLegend/FormLegend";
import PostOfficeBoxAddress from "../Common/Address/PostOfficeBoxAddress";
import CheckIcon from "../../../assets/icons/common/toggleable/IconCheck";
import CrossIcon from "../../../assets/icons/common/toggleable/IconClose";
import { translate } from "../../../services/translations/translations";
import RentalContractPropertyAddress from "./RentalContractPropertyAddress";
import _includes from "lodash/includes";
import IconFamilyDetailed from "../../../assets/icons/generic/toggleable/IconFamilyDetailed";
import PersonsData from "../AbstractLitigationCase/PersonsData";
import FormSubmitProps from "../../../types/FormSubmitProps";
import { Person as PersonType } from "../../../types/Entities/Person";

type ContractPartnerProps = {
  submitProps: FormSubmitProps;
};

export default function ContractPartner({ submitProps }: ContractPartnerProps) {
  const { values, handleSubmit, handleChange } = useForm();

  //clears the additionalOpposingParties field when switching to singular
  useEffect(() => {
    if (values.opposingPartyCount === "singular") {
      const currentParties: PersonType = values.additionalOpposingParties;
      if (Array.isArray(currentParties) && currentParties.length > 0) {
        const idsToDelete = currentParties.map((p: PersonType) => p.id).filter((id: number) => id);

        if (idsToDelete.length > 0) {
          const currentRemoveList = values.additionalOpposingPartiesToBeRemoved || [];
          handleChange({
            target: {
              name: "additionalOpposingPartiesToBeRemoved",
              value: [...currentRemoveList, ...idsToDelete],
            },
          });
        }

        handleChange({
          target: { name: "additionalOpposingParties", value: [] },
        });
      }
    }
  }, [values.opposingPartyCount]);

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Ihr Vertrag
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
            }}
          >
            <Grid size={12}>
              <FormLegend>
                <strong>Angaben zu Ihrem {values.contractPartyType === "landlord" ? "Mieter" : "Vermieter"}</strong>
              </FormLegend>
            </Grid>
            <Grid sx={[wizardStyle.gridTopSpaced, wizardStyle.question]} size={12}>
              <FormLegend>
                {values.contractPartyType === "landlord"
                  ? "Damit wir rechtliche Schritte einleiten können, benötigen wir möglichst ausführliche Angaben zu Ihrem Mieter. Die Daten sollten mit den Angaben Ihres Mieters im Mietvertrag übereinstimmen."
                  : "Damit wir rechtliche Schritte einleiten können, benötigen wir möglichst ausführliche Angaben zu Ihrem Vermieter. Die Daten sollten Sie in Ihrem Mietvertrag oder in anderem Schriftverkehr mit dem Vermieter finden können."}
              </FormLegend>
            </Grid>
            <Grid size={12}>
              <Group
                type="radio"
                name="opposingPartyType"
                question={translate(`rentalContract.opposingPartyType.question`, values)}
                options={[
                  {
                    labelText: translate(`rentalContract.opposingPartyType.options.opposingPartyPerson.label`, values),
                    labelIcon: <PersonIcon />,
                    labelIconActive: <PersonIcon active />,
                    value: "opposingPartyPerson",
                  },
                  {
                    labelText: translate(`rentalContract.opposingPartyType.options.opposingPartyLegalEntity.label`, values),
                    labelIcon: <LegalEntityIcon />,
                    labelIconActive: <LegalEntityIcon active />,
                    value: "opposingPartyLegalEntity",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            {values.opposingPartyType === "opposingPartyPerson" && values.contractPartyType === "landlord" && (
              <Grid size={12}>
                <Group
                  type="radio"
                  name="opposingPartyCount"
                  question={"Haben Sie den Mietvertrag mit einer oder mehreren Personen geschlossen?"}
                  options={[
                    {
                      labelText: "Ich habe den Mietvertrag mit einer einzelnen Person geschlossen ",
                      labelIcon: <PersonIcon />,
                      labelIconActive: <PersonIcon active />,
                      value: "singular",
                    },
                    {
                      labelText: "Ich habe den Mietvertrag mit zwei oder mehr Personen geschlossen",
                      labelIcon: <IconFamilyDetailed />,
                      labelIconActive: <IconFamilyDetailed active />,
                      value: "multiple",
                    },
                  ]}
                  validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                />
              </Grid>
            )}
            {(values.opposingPartyCount || values.contractPartyType !== "landlord") && values.opposingPartyType === "opposingPartyPerson" && (
              <>
                <Person path="opposingPartyPerson" headline={translate("rentalContract.opposingPartyPerson.headline", values)} />
                <Grid sx={[wizardStyle.gridTopSpaced, wizardStyle.question]} size={12}>
                  <FormLegend>{translate("rentalContract.opposingPartyPerson.question", values)}</FormLegend>
                </Grid>
                <Address path="opposingPartyPerson.residenceAddress" headline="" fieldsAreRequired={false} />
                <Grid size={12}>
                  <ValidatorTextField name="opposingPartyPerson.email" label="E-Mail-Adresse" autoComplete="email" validators={[emailDefault]} />
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField name="opposingPartyPerson.telephone" label="Telefonnummer" autoComplete="tel" validators={isPhoneNumberDefault} />
                </Grid>
              </>
            )}
            {values.opposingPartyCount === "multiple" && <PersonsData collectionName={"additionalOpposingParties"} withAddress={false} />}
            {values.opposingPartyType === "opposingPartyLegalEntity" && (
              <>
                <Grid sx={[wizardStyle.gridTopSpaced, wizardStyle.question]} size={12}>
                  <FormLegend>
                    {values.contractPartyType === "landlord"
                      ? "Wie heißt das Unternehmen, an das Sie vermieten?"
                      : "Wie heißt das Unternehmen, das an Sie vermietet?"}
                  </FormLegend>
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="opposingPartyLegalEntity.organization.legalName"
                    label="Name des Unternehmens"
                    helperText="Bitte geben Sie die vollständige Firma an, die auch im Mietvertrag oder im Briefkopf in Schriftsätzen genannt wird. Also z.B. Gebäudeservice GmbH"
                    validators={[requiredValidator()]}
                  />
                </Grid>
                <Grid sx={[wizardStyle.gridTopSpaced, wizardStyle.question]} size={12}>
                  <FormLegend>
                    Bitte geben Sie möglichst alle weiteren Daten an, die Ihnen vorliegen. Sollten Ihnen nicht alle Daten vorliegen, lassen Sie die Felder gerne
                    einfach leer.
                  </FormLegend>
                </Grid>
                <Address path="opposingPartyLegalEntity.organization.address" headline={""} fieldsAreRequired={false} />
                <PostOfficeBoxAddress path="opposingPartyLegalEntity.organization.address" headline={""} fieldsAreRequired={false} />
                <Grid size={12}>
                  <ValidatorTextField
                    name="opposingPartyLegalEntity.organization.mainEmail.email"
                    label="E-Mail-Adresse"
                    autoComplete="email"
                    validators={[emailDefault]}
                  />
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="opposingPartyLegalEntity.organization.telephone"
                    label="Telefonnummer"
                    autoComplete="tel"
                    validators={isPhoneNumberDefault}
                  />
                </Grid>
              </>
            )}
            {_includes(["enforceClaim", "defendClaim"], values.caseCategory) && <RentalContractPropertyAddress />}
            <Grid size={12}>
              <Group
                type="radio"
                name="hasPropertyManager"
                question={translate(`rentalContract.hasPropertyManager.question`, values)}
                options={[
                  {
                    labelText: translate(`rentalContract.hasPropertyManager.options.yes.label`, values),
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                  {
                    labelText: translate(`rentalContract.hasPropertyManager.options.no.label`, values),
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            {values.hasPropertyManager === "yes" && (
              <>
                <Grid sx={[wizardStyle.gridTopSpaced, wizardStyle.question]} size={12}>
                  <FormLegend>
                    Bitte geben Sie möglichst alle Daten an, die Ihnen vorliegen, damit wir schnellstmöglich für Sie tätig werden und die Hausverwaltung direkt
                    kontaktieren können.
                  </FormLegend>
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="propertyManager.organization.legalName"
                    label="Name Hausverwaltung"
                    helperText="Bitte geben Sie den vollständigen Firmennamen an, der im Briefkopf der Schreiben oder in der E-Mail-Signatur steht. Z.B.: Hausverwaltungs GmbH"
                    validators={[requiredValidator()]}
                  />
                </Grid>
                <Address path="propertyManager.organization.address" headline={""} fieldsAreRequired={false} />
                <Grid size={12}>
                  <ValidatorTextField
                    name="propertyManager.organization.mainEmail.email"
                    label="E-Mail-Adresse"
                    autoComplete="email"
                    validators={[emailDefault]}
                  />
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="propertyManager.organization.telephone"
                    label="Telefonnummer"
                    autoComplete="tel"
                    validators={isPhoneNumberDefault}
                  />
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField name="propertyManager.organization.fax" label="Faxnummer" validators={isPhoneNumberDefault} />
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="propertyManagerReference"
                    label="Aktenzeichen Hausverwaltung"
                    helperText="Benutzt die Hausverwaltung in Schreiben an Sie im Betreff oder dem Briefkopf ein Zeichen/ eine Nummer?"
                  />
                </Grid>
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
